/*
 * Intercepta o clique nos botoes do CORPO do CMS quando o visitante nao esta logado.
 *
 * Esses botoes sao HTML do CMS -- `<a class="article__button article__button--[cor]" href="...">`
 * --, entao nao passam por componente: a unica forma de mudar o comportamento e delegacao no
 * elemento do corpo. O href continua no DOM, que e o que o crawler le e o que permite abrir em
 * nova aba.
 *
 * Sem isto o deslogado ia para a rota da feature, tomava redirect para a home e autenticava la,
 * perdendo o destino. Agora abre a mesma modal dos CTAs e o destino chega ao pos-login pelo
 * `<meta name="auth-redirect-route">`, que o `ContainerReducer` do AuthFunctional le e repassa
 * como `redirect_to` para `auth/validate` -- e que vence o `?refer` da URL.
 *
 * O valor anterior da meta e restaurado no `CloseStyledModal`: quem fecha a modal sem se
 * cadastrar nao deve deixar o destino deste botao valendo para o proximo CTA da pagina.
 *
 * Os botoes recebem `PAGELOADER_SKIP_ATTRIBUTE`: sem ele o `usePageLoader` intercepta o clique
 * em `capture` no `document` e a delegacao daqui nunca roda.
 */
import { useEffect } from 'react';

import { PAGELOADER_SKIP_ATTRIBUTE } from '../../Common/PageLoader/usePageLoader';

const BUTTON_SELECTOR = 'a.article__button';
const META_NAME = 'auth-redirect-route';
const BODY_ID = 'article_body';

interface IContentAuthGate {
    isLogged: boolean;
    authStep: string;
}

const useContentAuthGate = ({ isLogged, authStep }: IContentAuthGate): void => {
    useEffect(() => {
        if (isLogged || !authStep) return undefined;

        const body = document.getElementById(BODY_ID);

        if (!body) return undefined;

        let previousContent: string | null = null;
        let createdMeta: HTMLMetaElement | null = null;

        const restore = (): void => {
            if (createdMeta) {
                createdMeta.remove();
                createdMeta = null;

                return;
            }

            const meta = document.getElementsByName(META_NAME)[0] as HTMLMetaElement | undefined;

            if (meta && previousContent !== null) meta.content = previousContent;

            previousContent = null;
        };

        const handleClick = (event: Event): void => {
            const target = event.target as HTMLElement | null;
            const button = target?.closest?.(BUTTON_SELECTOR) as HTMLAnchorElement | null;

            if (!button) return;

            const href = button.getAttribute('href');

            if (!href) return;

            event.preventDefault();

            let meta = document.getElementsByName(META_NAME)[0] as HTMLMetaElement | undefined;

            if (meta) {
                previousContent = meta.content;
            } else {
                meta = document.createElement('meta');
                meta.name = META_NAME;
                document.head.appendChild(meta);
                createdMeta = meta;
            }

            meta.content = href;

            if (typeof window.onOpenStyledModal === 'function') window.onOpenStyledModal(authStep);
        };

        const buttons = Array.from(body.querySelectorAll<HTMLAnchorElement>(BUTTON_SELECTOR));

        buttons.forEach((button) => button.setAttribute(PAGELOADER_SKIP_ATTRIBUTE, ''));

        body.addEventListener('click', handleClick);
        document.addEventListener('CloseStyledModal', restore);

        return () => {
            buttons.forEach((button) => button.removeAttribute(PAGELOADER_SKIP_ATTRIBUTE));
            body.removeEventListener('click', handleClick);
            document.removeEventListener('CloseStyledModal', restore);
            restore();
        };
    }, [isLogged, authStep]);
};

export default useContentAuthGate;
